// The field sampler (spike 002, gate E2; decision 0064's "field performance"): frame times from real play, kept in a
// fixed ring (no growth, no allocation per frame), summed up as percentiles when the game takes a sample. Production
// safe: no three.js, no DOM. `@rpg/telemetry` sends what `take()` returns; the profiler overlay can read `peek()`.
import { r2, summarize } from "./stats";

/** One window's frame times. */
export interface FieldSample {
  /** Frame-time percentiles, ms. */
  frameMs: { p50: number; p95: number; p99: number; worst: number; mean: number };
  /** Frames in the window. */
  frames: number;
  /** Frames over the budget. */
  slowFrames: number;
  /** The budget the slow count used, ms. */
  budgetMs: number;
}

/** The sampler. */
export interface FieldSampler {
  /** One frame's time, ms (frames over `maxFrameMs` are ignored: a tab switch, a breakpoint). */
  frame(ms: number): void;
  /** The window so far, without clearing it (null when empty). */
  peek(): FieldSample | null;
  /** The window so far, then a fresh window (null when empty). */
  take(): FieldSample | null;
  /** Frames in the window. */
  count(): number;
}

/** Make a field sampler: `capacity` frames kept (the newest), `budgetMs` for the slow count. */
export function createFieldSampler(o: { capacity?: number; budgetMs: number; maxFrameMs?: number }): FieldSampler {
  const cap = Math.max(1, Math.floor(o.capacity ?? 3600));
  const ring = new Float32Array(cap);
  const maxFrame = o.maxFrameMs ?? 1000;
  let n = 0;
  let head = 0;
  let slow = 0;
  const peek = (): FieldSample | null => {
    if (!n) return null;
    const vals = Array.from(ring.subarray(0, Math.min(n, cap)));
    return { frameMs: summarize(vals), frames: n, slowFrames: slow, budgetMs: r2(o.budgetMs) };
  };
  return {
    frame(ms) {
      if (!Number.isFinite(ms) || ms < 0 || ms > maxFrame) return;
      ring[head] = ms;
      head = (head + 1) % cap;
      n++;
      if (ms > o.budgetMs) slow++;
    },
    peek,
    take() {
      const s = peek();
      n = 0;
      head = 0;
      slow = 0;
      return s;
    },
    count: () => n,
  };
}
